/**
 * The slides artefact's preset geometry and typography table (plans/SLIDES_ARTEFACT.md, "PPTX
 * export": "the shared renderer must implement each allowed preset (outline, default adj, text
 * rectangle) from presetShapeDefinitions").
 *
 * WHY A TABLE. A slide is drawn twice: by the web renderer (the editor canvas, the thumbnails and the
 * AI's render_slide) and by PowerPoint, from the export's `a:prstGeom`. PowerPoint computes a
 * preset's outline and text rectangle from ECMA-376's presetShapeDefinitions.xml, so the renderer
 * has to compute the SAME ones, or text that fits on the canvas wraps letter by letter in the deck
 * (spike A: a chevron's text sits inside its point depth, not in the full box). The same goes for
 * line height (CSS multiplies the font size, PowerPoint's 100 % is ≈ 1.2 × it) and bullets.
 *
 * THE CONTRACT. `PRESETS` is a transcription of the 10 allowed presets' definitions (default adjust
 * values, guide formulas, text rectangle, connection sites, path), and `evaluatePreset` runs those
 * formulas the way PowerPoint does for a w × h box. Lengths are in the shape's own box (canvas px),
 * adjust values in 1/100000, angles in 1/60000 degree.
 *
 * CONNECTORS. `connectionSites` places an element's connection sites (its preset's cxnLst, the
 * rect's for any other element) on the slide, rotated with it; `routeConnector` routes a line or an
 * elbow connector between two ends, as both the renderer and the export (`p:cxnSp`) draw it.
 *
 * Pure: no DOM, no React. Parity: `backend/domain/slide-presets.ts` holds an identical copy (the web
 * app cannot import backend source), mechanically pinned by `shared/slide-presets.json` (the table:
 * both copies must equal it) and `shared/slide-presets.fixture.json` (evaluated cases: both
 * evaluators must reproduce them). Keep the two in sync — the same arrangement as
 * table-geometry.ts.
 */

// ── typography ──────────────────────────────────────────────────────────────────────────────────

/**
 * CSS `line-height: normal` and PowerPoint's 100 % line spacing are both ≈ 1.2 × the font size, so a
 * CSS line height maps to `a:spcPct = lineHeight / LINE_HEIGHT_BASE` and the renderer uses this base
 * wherever a text has no explicit line height.
 */
export const LINE_HEIGHT_BASE = 1.2
/** The bullet of an unordered list, at every level. */
export const BULLET_CHAR = '•'
/** Indent per list level, in canvas px (level 0 is indented once). */
export const LIST_INDENT_PX = 36
/** How far the bullet hangs left of the item's text, in canvas px. */
export const LIST_HANG_PX = 24
/** The deepest list level (0-based): PowerPoint's `lvl` stops at 8, the editor's indent too (AB#285). */
export const LIST_MAX_LEVEL = 8

/** The `a:spcPct` value (1/1000 of a percent) for a unitless CSS line height. */
export function lineSpacingPct(lineHeight: number): number {
  return Math.round((lineHeight / LINE_HEIGHT_BASE) * 100000)
}

/**
 * Where a list item at `level` (0-based) sits: its text starts `marginLeft` px from the box's
 * content edge, and its bullet `hanging` px to the left of that.
 */
export function listIndent(level: number): { marginLeft: number; hanging: number } {
  return { marginLeft: (Math.max(0, Math.floor(level)) + 1) * LIST_INDENT_PX, hanging: LIST_HANG_PX }
}

// ── preset table ────────────────────────────────────────────────────────────────────────────────

export type PresetName = 'rect' | 'roundRect' | 'ellipse' | 'line' | 'arrow' | 'chevron' | 'triangle' | 'diamond' | 'homePlate' | 'elbow'

/** One path command; its arguments are guide names or integer literals. `A` = DrawingML arcTo (wR hR stAng swAng). */
export type PresetPathCommand = ['M', string, string] | ['L', string, string] | ['A', string, string, string, string] | ['Z']

export interface PresetDef {
  /** The DrawingML preset name (`a:prstGeom prst`). */
  prst: string
  /** Default adjust values, in 1/100000. */
  avLst: Record<string, number>
  /** Guide formulas, `[name, "op arg arg arg"]`, evaluated in order. */
  gdLst: [string, string][]
  /** The text rectangle, as guide names. */
  textRect: { l: string; t: string; r: string; b: string }
  /**
   * The connection sites, in PowerPoint's order (the `idx` of a connector's `a:stCxn`): where a
   * connector glues, as guide names, and `ang`, the direction it leaves the site (1/60000 degree,
   * 0 = right, cd4 = down).
   */
  cxnLst: { ang: string; x: string; y: string }[]
  path: { fill: boolean; commands: PresetPathCommand[] }
}

export const PRESETS: Record<PresetName, PresetDef> = {
  rect: {
    prst: 'rect',
    avLst: {},
    gdLst: [],
    textRect: { l: 'l', t: 't', r: 'r', b: 'b' },
    cxnLst: [{ ang: '3cd4', x: 'hc', y: 't' }, { ang: 'cd2', x: 'l', y: 'vc' }, { ang: 'cd4', x: 'hc', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: { fill: true, commands: [['M', 'l', 't'], ['L', 'r', 't'], ['L', 'r', 'b'], ['L', 'l', 'b'], ['Z']] },
  },
  roundRect: {
    prst: 'roundRect',
    avLst: { adj: 16667 },
    gdLst: [
      ['a', 'pin 0 adj 50000'],
      ['dx1', '*/ ss a 100000'],
      ['x2', '+- r 0 dx1'],
      ['y2', '+- b 0 dx1'],
      ['il', '*/ dx1 29289 100000'],
      ['ir', '+- r 0 il'],
      ['ib', '+- b 0 il'],
    ],
    textRect: { l: 'il', t: 'il', r: 'ir', b: 'ib' },
    cxnLst: [{ ang: '3cd4', x: 'hc', y: 't' }, { ang: 'cd2', x: 'l', y: 'vc' }, { ang: 'cd4', x: 'hc', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: {
      fill: true,
      commands: [
        ['M', 'l', 'dx1'],
        ['A', 'dx1', 'dx1', 'cd2', 'cd4'],
        ['L', 'x2', 't'],
        ['A', 'dx1', 'dx1', '3cd4', 'cd4'],
        ['L', 'r', 'y2'],
        ['A', 'dx1', 'dx1', '0', 'cd4'],
        ['L', 'dx1', 'b'],
        ['A', 'dx1', 'dx1', 'cd4', 'cd4'],
        ['Z'],
      ],
    },
  },
  ellipse: {
    prst: 'ellipse',
    avLst: {},
    gdLst: [
      ['idx', 'cos wd2 2700000'],
      ['idy', 'sin hd2 2700000'],
      ['il', '+- hc 0 idx'],
      ['ir', '+- hc idx 0'],
      ['it', '+- vc 0 idy'],
      ['ib', '+- vc idy 0'],
    ],
    textRect: { l: 'il', t: 'it', r: 'ir', b: 'ib' },
    cxnLst: [
      { ang: '3cd4', x: 'hc', y: 't' },
      { ang: '3cd4', x: 'il', y: 'it' },
      { ang: 'cd2', x: 'l', y: 'vc' },
      { ang: 'cd4', x: 'il', y: 'ib' },
      { ang: 'cd4', x: 'hc', y: 'b' },
      { ang: 'cd4', x: 'ir', y: 'ib' },
      { ang: '0', x: 'r', y: 'vc' },
      { ang: '3cd4', x: 'ir', y: 'it' },
    ],
    path: {
      fill: true,
      commands: [
        ['M', 'l', 'vc'],
        ['A', 'wd2', 'hd2', 'cd2', 'cd4'],
        ['A', 'wd2', 'hd2', '3cd4', 'cd4'],
        ['A', 'wd2', 'hd2', '0', 'cd4'],
        ['A', 'wd2', 'hd2', 'cd4', 'cd4'],
        ['Z'],
      ],
    },
  },
  line: {
    prst: 'line',
    avLst: {},
    gdLst: [],
    textRect: { l: 'l', t: 't', r: 'r', b: 'b' },
    cxnLst: [],
    path: { fill: false, commands: [['M', 'l', 't'], ['L', 'r', 'b']] },
  },
  arrow: {
    prst: 'rightArrow',
    avLst: { adj1: 50000, adj2: 50000 },
    gdLst: [
      ['maxAdj2', '*/ 100000 w ss'],
      ['a1', 'pin 0 adj1 100000'],
      ['a2', 'pin 0 adj2 maxAdj2'],
      ['dx1', '*/ ss a2 100000'],
      ['x1', '+- r 0 dx1'],
      ['dy1', '*/ h a1 200000'],
      ['y1', '+- vc 0 dy1'],
      ['y2', '+- vc dy1 0'],
      ['dx2', '*/ y1 dx1 hd2'],
      ['x2', '+- x1 dx2 0'],
    ],
    textRect: { l: 'l', t: 'y1', r: 'x2', b: 'y2' },
    cxnLst: [{ ang: '3cd4', x: 'x1', y: 't' }, { ang: 'cd2', x: 'l', y: 'vc' }, { ang: 'cd4', x: 'x1', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: {
      fill: true,
      commands: [
        ['M', 'l', 'y1'],
        ['L', 'x1', 'y1'],
        ['L', 'x1', 't'],
        ['L', 'r', 'vc'],
        ['L', 'x1', 'b'],
        ['L', 'x1', 'y2'],
        ['L', 'l', 'y2'],
        ['Z'],
      ],
    },
  },
  chevron: {
    prst: 'chevron',
    avLst: { adj: 50000 },
    gdLst: [
      ['maxAdj', '*/ 100000 w ss'],
      ['a', 'pin 0 adj maxAdj'],
      ['x1', '*/ ss a 100000'],
      ['x2', '+- r 0 x1'],
      ['x3', '*/ x2 1 2'],
      ['dx', '+- x2 0 x1'],
      ['il', '?: dx x1 l'],
      ['ir', '?: dx x2 r'],
    ],
    textRect: { l: 'il', t: 't', r: 'ir', b: 'b' },
    cxnLst: [{ ang: '3cd4', x: 'x3', y: 't' }, { ang: 'cd2', x: 'x1', y: 'vc' }, { ang: 'cd4', x: 'x3', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: {
      fill: true,
      commands: [['M', 'l', 't'], ['L', 'x2', 't'], ['L', 'r', 'vc'], ['L', 'x2', 'b'], ['L', 'l', 'b'], ['L', 'x1', 'vc'], ['Z']],
    },
  },
  triangle: {
    prst: 'triangle',
    avLst: { adj: 50000 },
    gdLst: [
      ['a', 'pin 0 adj 100000'],
      ['x1', '*/ w a 200000'],
      ['x2', '*/ w a 100000'],
      ['x3', '+- x1 wd2 0'],
    ],
    textRect: { l: 'x1', t: 'vc', r: 'x3', b: 'b' },
    cxnLst: [
      { ang: '3cd4', x: 'x2', y: 't' },
      { ang: 'cd2', x: 'x1', y: 'vc' },
      { ang: 'cd4', x: 'l', y: 'b' },
      { ang: 'cd4', x: 'x2', y: 'b' },
      { ang: 'cd4', x: 'r', y: 'b' },
      { ang: '0', x: 'x3', y: 'vc' },
    ],
    path: { fill: true, commands: [['M', 'l', 'b'], ['L', 'x2', 't'], ['L', 'r', 'b'], ['Z']] },
  },
  diamond: {
    prst: 'diamond',
    avLst: {},
    gdLst: [
      ['ir', '*/ w 3 4'],
      ['ib', '*/ h 3 4'],
    ],
    textRect: { l: 'wd4', t: 'hd4', r: 'ir', b: 'ib' },
    cxnLst: [{ ang: '3cd4', x: 'hc', y: 't' }, { ang: 'cd2', x: 'l', y: 'vc' }, { ang: 'cd4', x: 'hc', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: { fill: true, commands: [['M', 'l', 'vc'], ['L', 'hc', 't'], ['L', 'r', 'vc'], ['L', 'hc', 'b'], ['Z']] },
  },
  homePlate: {
    prst: 'homePlate',
    avLst: { adj: 50000 },
    gdLst: [
      ['maxAdj', '*/ 100000 w ss'],
      ['a', 'pin 0 adj maxAdj'],
      ['dx1', '*/ ss a 100000'],
      ['x1', '+- r 0 dx1'],
      ['ir', '+/ x1 r 2'],
      ['x2', '*/ x1 1 2'],
    ],
    textRect: { l: 'l', t: 't', r: 'ir', b: 'b' },
    cxnLst: [{ ang: '3cd4', x: 'x2', y: 't' }, { ang: 'cd2', x: 'l', y: 'vc' }, { ang: 'cd4', x: 'x2', y: 'b' }, { ang: '0', x: 'r', y: 'vc' }],
    path: { fill: true, commands: [['M', 'l', 't'], ['L', 'x1', 't'], ['L', 'r', 'vc'], ['L', 'x1', 'b'], ['L', 'l', 'b'], ['Z']] },
  },
  elbow: {
    prst: 'bentConnector3',
    avLst: { adj1: 50000 },
    gdLst: [['x1', '*/ w adj1 100000']],
    textRect: { l: 'l', t: 't', r: 'r', b: 'b' },
    cxnLst: [],
    path: { fill: false, commands: [['M', 'l', 't'], ['L', 'x1', 't'], ['L', 'x1', 'b'], ['L', 'r', 'b']] },
  },
}

export const PRESET_NAMES = Object.keys(PRESETS) as PresetName[]

export function isPresetName(name: unknown): name is PresetName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(PRESETS, name)
}

/** The DrawingML preset for a model shape name (`arrow` → `rightArrow`); anything unknown is a rect. */
export function presetPrst(name: string | undefined): string {
  return isPresetName(name) ? PRESETS[name].prst : 'rect'
}

/** A roundRect's `adj` for a CSS border radius on a w × h box (radius / min(w, h), capped at 50 %). */
export function roundRectAdj(radius: number, w: number, h: number): number {
  const ss = Math.min(w, h)
  if (!(ss > 0) || !Number.isFinite(radius)) return 0
  return Math.max(0, Math.min(50000, Math.round((radius / ss) * 100000)))
}

// ── evaluator ───────────────────────────────────────────────────────────────────────────────────

export interface PresetGeometry {
  prst: string
  /** The adjust values used: the defaults, overridden by the caller's. */
  adj: Record<string, number>
  /** The outline as an SVG path in the box's coordinates (absolute commands, numbers to 1/100). */
  path: string
  /** False for an open outline (a line): stroke only. */
  filled: boolean
  /** Where the text goes, in the box's coordinates; padding applies inside it. */
  textRect: { x: number; y: number; w: number; h: number }
}

/** 1/60000 degree → radians. */
const RAD = Math.PI / 180 / 60000
const round = (n: number): number => Math.round(n * 100) / 100 + 0

/** ECMA-376's built-in guides for a w × h box. */
function builtins(w: number, h: number): Map<string, number> {
  const ss = Math.min(w, h)
  const ls = Math.max(w, h)
  return new Map<string, number>([
    ['w', w], ['h', h], ['l', 0], ['t', 0], ['r', w], ['b', h], ['hc', w / 2], ['vc', h / 2],
    ['wd2', w / 2], ['wd3', w / 3], ['wd4', w / 4], ['wd5', w / 5], ['wd6', w / 6], ['wd8', w / 8], ['wd10', w / 10], ['wd32', w / 32],
    ['hd2', h / 2], ['hd3', h / 3], ['hd4', h / 4], ['hd5', h / 5], ['hd6', h / 6], ['hd8', h / 8], ['hd10', h / 10],
    ['ss', ss], ['ls', ls], ['ssd2', ss / 2], ['ssd4', ss / 4], ['ssd6', ss / 6], ['ssd8', ss / 8], ['ssd16', ss / 16], ['ssd32', ss / 32],
    ['cd2', 10800000], ['cd4', 5400000], ['cd8', 2700000], ['3cd4', 16200000], ['3cd8', 8100000], ['5cd8', 13500000], ['7cd8', 18900000],
  ])
}

function value(guides: Map<string, number>, arg: string | undefined): number {
  if (arg === undefined) throw new Error('slide-presets: missing formula argument')
  if (/^-?\d+$/.test(arg)) return Number(arg)
  const v = guides.get(arg)
  if (v === undefined) throw new Error(`slide-presets: unknown guide "${arg}"`)
  return v
}

/** A division that degrades to 0 on a zero divisor (a 0-height line), never NaN. */
const div = (n: number, d: number): number => (d === 0 ? 0 : n / d)

/** One DrawingML guide formula. */
function formula(guides: Map<string, number>, fmla: string): number {
  const [op, ...args] = fmla.split(' ')
  const [x = 0, y = 0, z = 0] = args.map((a) => value(guides, a))
  switch (op) {
    case 'val': return x
    case '*/': return div(x * y, z)
    case '+-': return x + y - z
    case '+/': return div(x + y, z)
    case '?:': return x > 0 ? y : z
    case 'abs': return Math.abs(x)
    case 'at2': return Math.atan2(y, x) / RAD
    case 'cat2': return x * Math.cos(Math.atan2(z, y))
    case 'sat2': return x * Math.sin(Math.atan2(z, y))
    case 'cos': return x * Math.cos(y * RAD)
    case 'sin': return x * Math.sin(y * RAD)
    case 'tan': return x * Math.tan(y * RAD)
    case 'max': return Math.max(x, y)
    case 'min': return Math.min(x, y)
    case 'mod': return Math.sqrt(x * x + y * y + z * z)
    case 'pin': return y < x ? x : y > z ? z : y
    case 'sqrt': return Math.sqrt(x)
    default: throw new Error(`slide-presets: unknown formula op "${op}"`)
  }
}

/** A point on an ellipse at a VISUAL angle (as DrawingML's arcTo measures it), relative to its centre. */
function onEllipse(wR: number, hR: number, ang: number): [number, number] {
  const t = Math.atan2(wR * Math.sin(ang * RAD), hR * Math.cos(ang * RAD))
  return [wR * Math.cos(t), hR * Math.sin(t)]
}

/** A preset's guides for a w × h box: the built-ins, the adjust values used, then its formulas in order. */
function evaluateGuides(def: PresetDef, w: number, h: number, adj: Readonly<Record<string, number>>): { guides: Map<string, number>; used: Record<string, number> } {
  const guides = builtins(w, h)
  const used: Record<string, number> = {}
  for (const [k, d] of Object.entries(def.avLst)) {
    const given = adj[k]
    const v = given !== undefined && Number.isFinite(given) ? given : d
    used[k] = v
    guides.set(k, v)
  }
  for (const [gname, fmla] of def.gdLst) guides.set(gname, formula(guides, fmla))
  return { guides, used }
}

/**
 * Evaluates a preset for a w × h box: its outline, text rectangle and resolved adjust values.
 * `adj` overrides the defaults by name (unknown names and non-finite values are ignored).
 */
export function evaluatePreset(name: PresetName, w: number, h: number, adj: Readonly<Record<string, number>> = {}): PresetGeometry {
  const def = PRESETS[name]
  const { guides, used } = evaluateGuides(def, w, h, adj)

  const parts: string[] = []
  let cx = 0
  let cy = 0
  for (const cmd of def.path.commands) {
    if (cmd[0] === 'Z') {
      parts.push('Z')
    } else if (cmd[0] === 'A') {
      const wR = value(guides, cmd[1])
      const hR = value(guides, cmd[2])
      const st = value(guides, cmd[3])
      const sw = value(guides, cmd[4])
      const [sx, sy] = onEllipse(wR, hR, st)
      const [ex, ey] = onEllipse(wR, hR, st + sw)
      cx = cx - sx + ex
      cy = cy - sy + ey
      const large = Math.abs(sw) > 10800000 ? 1 : 0
      const sweep = sw > 0 ? 1 : 0
      parts.push(`A ${round(wR)} ${round(hR)} 0 ${large} ${sweep} ${round(cx)} ${round(cy)}`)
    } else {
      cx = value(guides, cmd[1])
      cy = value(guides, cmd[2])
      parts.push(`${cmd[0]} ${round(cx)} ${round(cy)}`)
    }
  }

  const l = value(guides, def.textRect.l)
  const t = value(guides, def.textRect.t)
  const r = value(guides, def.textRect.r)
  const b = value(guides, def.textRect.b)
  return {
    prst: def.prst,
    adj: used,
    path: parts.join(' '),
    filled: def.path.fill,
    textRect: { x: round(l), y: round(t), w: round(r - l), h: round(b - t) },
  }
}

// ── connectors ──────────────────────────────────────────────────────────────────────────────────

/** An element's box as the slide places it: slide coordinates, rotated by `rot` degrees about its centre. */
export interface PlacedBox {
  x: number
  y: number
  w: number
  h: number
  rot: number
}

/**
 * One connection site in slide coordinates: where a connector glues, and `ang`, the direction it
 * leaves the element (degrees, 0 = right, 90 = down: the site's DrawingML angle plus the element's
 * rotation).
 */
export interface ConnectionSite {
  x: number
  y: number
  ang: number
}

/** The connectors themselves: a line or an elbow connector has no connection site. */
export const CONNECTOR_PRESETS = ['line', 'elbow'] as const
export type ConnectorPreset = (typeof CONNECTOR_PRESETS)[number]

export function isConnectorPreset(name: unknown): name is ConnectorPreset {
  return name === 'line' || name === 'elbow'
}

/** Rotate (px, py) by `deg` degrees (clockwise on screen) about (cx, cy). */
function rotateAbout(px: number, py: number, cx: number, cy: number, deg: number): [number, number] {
  if (!deg) return [px, py]
  const r = (deg * Math.PI) / 180
  const dx = px - cx
  const dy = py - cy
  return [cx + dx * Math.cos(r) - dy * Math.sin(r), cy + dx * Math.sin(r) + dy * Math.cos(r)]
}

/** An angle in degrees, in [0, 360), to 1/100. */
const normDeg = (d: number): number => round(((d % 360) + 360) % 360) % 360

/** The presets that take a box's 20 connection sites too (AB#362): their own 4 cxnLst sites, then BOX_EXTRA_SITES. */
const BOX_SITE_PRESETS: ReadonlySet<PresetName> = new Set<PresetName>(['rect', 'roundRect', 'arrow', 'chevron', 'diamond', 'homePlate'])

/**
 * Does an element with this preset have a BOX's 20 connection sites, 5 per side (AB#325, AB#362)? The
 * rect, roundRect, arrow, chevron, diamond and homePlate, or anything that is not a preset shape
 * (`null`: a text box, an image, an icon, a table, a group). Indexes 0–3 stay the preset's own cxnLst
 * (for the chevron and the homePlate not the box mid-points); 4–19 are BOX_EXTRA_SITES on its bounding
 * box, some of them off a non-rectangular outline. The ellipse (8) and the triangle (6) keep their own.
 */
export function isBoxSites(name: PresetName | null): boolean {
  return name === null || BOX_SITE_PRESETS.has(name)
}

/**
 * The 16 sites a box-shaped element has past the rect's 4 mid-points (indexes 0–3: top, left, bottom,
 * right, PowerPoint's order — unchanged), at indexes 4–19: each side at 0, 25, 75 and 100 % of its
 * length, the sides top, right, bottom then left, each running clockwise. `[fx, fy, side]`: the point
 * as a fraction of the box, and its side's outward normal (degrees, 0 = right, 90 = down). A corner
 * carries two sites, one per side, at the same point, each facing out of its own side.
 */
export const BOX_EXTRA_SITES: readonly (readonly [number, number, number])[] = [
  [0, 0, 270], [0.25, 0, 270], [0.75, 0, 270], [1, 0, 270],
  [1, 0, 0], [1, 0.25, 0], [1, 0.75, 0], [1, 1, 0],
  [1, 1, 90], [0.75, 1, 90], [0.25, 1, 90], [0, 1, 90],
  [0, 1, 180], [0, 0.75, 180], [0, 0.25, 180], [0, 0, 180],
]

/** The rect's own 4 sites (top, left, bottom, right): the ones PowerPoint's rect has too. */
export const RECT_SITE_COUNT = 4

/**
 * The outward normal of the side a box-shaped element's site lies on (degrees, before the element's
 * rotation), or null when `site` is not one of its 20.
 */
export function boxSiteSide(site: number): number | null {
  if (site >= 0 && site < RECT_SITE_COUNT) return [270, 180, 90, 0][site] as number
  return BOX_EXTRA_SITES[site - RECT_SITE_COUNT]?.[2] ?? null
}

/** How many connection sites a preset has (`null`: an element that is not a preset shape, which has a box's 20). */
export function connectionSiteCount(name: PresetName | null): number {
  return isBoxSites(name) ? RECT_SITE_COUNT + BOX_EXTRA_SITES.length : PRESETS[name as PresetName].cxnLst.length
}

/**
 * The connection sites of an element, in PowerPoint's order: a preset shape's cxnLst (`name`; for
 * anything that is not a preset shape — `null`: a text box, an image, an icon, a table, a group — the
 * rect's top, left, bottom and right mid-points), followed for an element with a box's sites
 * (isBoxSites) by BOX_EXTRA_SITES. Evaluated in the element's own box with the default adjust values, then rotated
 * with it. Numbers to 1/100.
 */
export function connectionSites(name: PresetName | null, box: PlacedBox): ConnectionSite[] {
  const def = PRESETS[name ?? 'rect']
  const { guides } = evaluateGuides(def, box.w, box.h, {})
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  const place = (px: number, py: number, ang: number): ConnectionSite => {
    const [x, y] = rotateAbout(px, py, cx, cy, box.rot)
    return { x: round(x), y: round(y), ang: normDeg(ang + box.rot) }
  }
  const sites = def.cxnLst.map((c) => place(box.x + value(guides, c.x), box.y + value(guides, c.y), value(guides, c.ang) / 60000))
  if (!isBoxSites(name)) return sites
  return [...sites, ...BOX_EXTRA_SITES.map(([fx, fy, side]) => place(box.x + fx * box.w, box.y + fy * box.h, side))]
}

/** One end of a connector: a point, with the direction it leaves its site when it is glued to one. */
export interface ConnectorEnd {
  x: number
  y: number
  /** Degrees, 0 = right, 90 = down; absent for a free end. */
  ang?: number
}

/**
 * How far an elbow connector runs past its sites before it turns, in canvas px, when both sites face
 * the same way.
 */
export const ELBOW_STUB = 24

/** A connector's route between its two ends, for the renderer and the PPTX export alike. */
export interface ConnectorRoute {
  /** The `a:prstGeom prst` of the exported `p:cxnSp`. */
  prst: 'straightConnector1' | 'bentConnector3'
  /** The polyline, start to end, in slide coordinates. */
  points: Array<[number, number]>
  /**
   * The DrawingML transform PowerPoint keeps for it: the box of the two ends (a vertical elbow is a
   * bentConnector3 turned 90°, its extent swapped) and the flips that put the preset's start (its
   * top-left corner) on the start.
   */
  xfrm: { x: number; y: number; w: number; h: number; rot: 0 | 90; flipH: boolean; flipV: boolean }
  /** bentConnector3's `adj1`, in 1/100000 of the main axis (outside 0–100000 to run past a site). */
  adj?: number
}

const sgn = (n: number): 1 | -1 => (n < 0 ? -1 : 1)
const cosD = (deg: number): number => Math.cos((deg * Math.PI) / 180)
const sinD = (deg: number): number => Math.sin((deg * Math.PI) / 180)

/**
 * Routes a connector from `start` to `end`. A `line` is straight. An `elbow` is PowerPoint's
 * bentConnector3: it leaves the start along the axis its site faces (horizontal when the site faces
 * left or right; a free start takes the longer axis), turns once, and turns back onto that axis to
 * reach the end. The middle segment sits half-way when the sites face each other (adj 50 %), and
 * ELBOW_STUB px past both when they face the same way. `adj` (bentConnector3's adj1, in 1/100000 of
 * the main axis: where a member dragged the middle segment) places it instead, when the ends are
 * apart along that axis.
 */
export function routeConnector(geom: ConnectorPreset, start: ConnectorEnd, end: ConnectorEnd, adj?: number): ConnectorRoute {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const s: [number, number] = [round(start.x), round(start.y)]
  const e: [number, number] = [round(end.x), round(end.y)]
  const box = { x: round(Math.min(start.x, end.x)), y: round(Math.min(start.y, end.y)), w: round(Math.abs(dx)), h: round(Math.abs(dy)) }
  if (geom === 'line') return { prst: 'straightConnector1', points: [s, e], xfrm: { ...box, rot: 0, flipH: dx < 0, flipV: dy < 0 } }

  const sideways = (ang: number): boolean => Math.abs(cosD(ang)) >= Math.abs(sinD(ang)) - 1e-9
  const horizontal = start.ang !== undefined ? sideways(start.ang) : end.ang !== undefined ? sideways(end.ang) : Math.abs(dx) >= Math.abs(dy)
  const along = (p: ConnectorEnd): number => (horizontal ? p.x : p.y)
  const dir = (ang: number): 1 | -1 => sgn(horizontal ? cosD(ang) : sinD(ang))
  const m = along(end) - along(start)
  // A free end is met head-on.
  const d1 = start.ang !== undefined ? dir(start.ang) : sgn(m)
  const d2 = end.ang !== undefined ? dir(end.ang) : -sgn(m)
  let mid: number
  if (d1 === sgn(m) && d2 === -sgn(m)) mid = along(start) + m / 2
  else if (d1 === 1 && d2 === 1) mid = Math.max(along(start), along(end)) + ELBOW_STUB
  else if (d1 === -1 && d2 === -1) mid = Math.min(along(start), along(end)) - ELBOW_STUB
  // Facing away from each other: that takes more turns than bentConnector3 has.
  else mid = along(start) + m / 2
  if (adj !== undefined && Number.isFinite(adj) && m !== 0) mid = along(start) + (m * Math.round(adj)) / 100000
  const adj1 = m !== 0 ? Math.round((((mid - along(start)) * sgn(m)) / Math.abs(m)) * 100000) : 50000
  mid = round(mid)
  const points: Array<[number, number]> = horizontal ? [s, [mid, s[1]], [mid, e[1]], e] : [s, [s[0], mid], [e[0], mid], e]
  if (horizontal) return { prst: 'bentConnector3', points, xfrm: { ...box, rot: 0, flipH: dx < 0, flipV: dy < 0 }, adj: adj1 }
  const cx = (start.x + end.x) / 2
  const cy = (start.y + end.y) / 2
  const w = Math.abs(dy)
  const h = Math.abs(dx)
  return {
    prst: 'bentConnector3',
    points,
    xfrm: { x: round(cx - w / 2), y: round(cy - h / 2), w: round(w), h: round(h), rot: 90, flipH: dy < 0, flipV: dx > 0 },
    adj: adj1,
  }
}

/**
 * The model stores a line (and a connector's fallback) as a box whose top-left corner is its start and
 * whose bottom-right corner is its end, turned by `rot` about its centre — the model has no flips.
 * `lineBox` is that box for two ends (rot 0, 90, 180 or 270; numbers to 1/100), `lineEnds` its inverse.
 */
export function lineBox(start: { x: number; y: number }, end: { x: number; y: number }): PlacedBox {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const cx = (start.x + end.x) / 2
  const cy = (start.y + end.y) / 2
  const [rot, w, h] = dx >= 0 && dy >= 0 ? [0, dx, dy] : dx < 0 && dy < 0 ? [180, -dx, -dy] : dx >= 0 ? [270, -dy, dx] : [90, dy, -dx]
  return { x: round(cx - w / 2), y: round(cy - h / 2), w: round(w), h: round(h), rot }
}

export function lineEnds(box: PlacedBox): [{ x: number; y: number }, { x: number; y: number }] {
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  const [sx, sy] = rotateAbout(box.x, box.y, cx, cy, box.rot)
  const [ex, ey] = rotateAbout(box.x + box.w, box.y + box.h, cx, cy, box.rot)
  return [
    { x: round(sx), y: round(sy) },
    { x: round(ex), y: round(ey) },
  ]
}
